'use client';

import { useState, useEffect } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import AIRevenueGrowthROICalculator from "@/components/calculators/AIRevenueGrowthROICalculator";

interface Tool {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
}

const tools: Tool[] = [
  {
    id: 'ai-growth-impact-assessment',
    title: 'AI Growth Impact Assessment',
    description: 'Discover your organization\'s potential for revenue acceleration and market expansion through strategic AI implementation.',
    icon: 'ClipboardDocumentCheckIcon',
    color: 'bg-primary'
  },
  {
    id: 'support-cost-savings',
    title: 'Support Cost Savings Calculator',
    description: 'Discover potential efficiency gains and resource optimization through AI-powered customer support automation.',
    icon: 'CurrencyDollarIcon',
    color: 'bg-success'
  },
  {
    id: 'sales-pipeline-health',
    title: 'Sales Pipeline Health Check',
    description: 'Analyze conversion rates, identify bottlenecks, and uncover opportunities to optimize your sales pipeline performance.',
    icon: 'ChartBarIcon',
    color: 'bg-accent'
  }
];

export default function ToolsAssessmentsPage() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTool, setActiveTool] = useState<string | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleToolSelect = (toolId: string) => {
    setActiveTool(toolId);
  };

  const handleBackToTools = () => {
    setActiveTool(null);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="animate-pulse space-y-8">
              <div className="h-12 bg-muted rounded w-1/2 mx-auto" />
              <div className="h-6 bg-muted rounded w-2/3 mx-auto" />
              <div className="grid md:grid-cols-2 gap-8 mt-12">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-64 bg-muted rounded-xl" />
                ))}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        {!activeTool ? (
          <>
            {/* Hero Section */}
            <section className="bg-gradient-to-br from-primary/5 via-accent/5 to-background py-20 lg:py-32">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-4xl mx-auto">
                  <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full mb-6">
                    <Icon name="WrenchScrewdriverIcon" size={20} className="text-accent" />
                    <span className="text-sm font-semibold text-accent">Strategic Business Tools</span>
                  </div>
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-foreground mb-6">
                    Tools & Assessments
                  </h1>
                  <p className="text-xl text-muted-foreground mb-8">
                    Interactive resources designed to help you evaluate opportunities, calculate ROI, and make data-driven decisions about AI implementation in your business.
                  </p>
                </div>
              </div>
            </section>

            {/* Tools Grid */}
            <section className="py-20 lg:py-32">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid md:grid-cols-2 gap-8">
                  {tools.map((tool) => (
                    <div
                      key={tool.id}
                      className="bg-card border border-border rounded-2xl p-8 hover:shadow-elevated transition-all duration-300 cursor-pointer group"
                      onClick={() => handleToolSelect(tool.id)}
                    >
                      <div className={`inline-flex items-center justify-center w-16 h-16 ${tool.color} rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                        <Icon name={tool.icon} size={32} className="text-white" />
                      </div>
                      <h3 className="text-2xl font-heading font-bold text-foreground mb-4 group-hover:text-accent transition-colors">
                        {tool.title}
                      </h3>
                      <p className="text-muted-foreground mb-6">
                        {tool.description}
                      </p>
                      <div className="flex items-center text-accent font-semibold group-hover:gap-2 transition-all">
                        <span>Launch Tool</span>
                        <Icon name="ArrowRightIcon" size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section className="bg-gradient-to-br from-accent via-accent/90 to-primary py-20 lg:py-32">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-6">
                  Need Help Interpreting Your Results?
                </h2>
                <p className="text-xl text-white/90 mb-8">
                  Schedule a free consultation with our AI strategy experts to discuss your assessment results and create a customized implementation roadmap.
                </p>
                <Link
                  href="/contact"
                  className="inline-flex items-center px-8 py-4 bg-white text-accent font-heading font-semibold rounded-lg hover:bg-white/90 hover:shadow-elevated transition-all duration-300 transform hover:-translate-y-1"
                >
                  Schedule Free Consultation
                  <Icon name="ArrowRightIcon" size={20} className="ml-2" />
                </Link>
              </div>
            </section>
          </>
        ) : (
          <div className="py-20 lg:py-32">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <button
                onClick={handleBackToTools}
                className="flex items-center text-accent hover:text-accent/80 font-semibold mb-8 transition-colors"
              >
                <Icon name="ArrowLeftIcon" size={20} className="mr-2" />
                Back to Tools
              </button>
              {activeTool === 'ai-growth-impact-assessment' && <AIGrowthImpactAssessment />}
              {activeTool === 'support-cost-savings' && <SupportCostSavingsCalculator />}
              {activeTool === 'sales-pipeline-health' && <SalesPipelineHealthCheck />}
            </div>
          </div>
        )}
      </main>

      {/* AI Revenue Growth ROI Calculator Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                AI Revenue Growth ROI Calculator
              </h2>
              <p className="text-xl text-gray-600">
                Analyze the potential revenue impact and ROI of implementing AI-powered sales automation
              </p>
            </div>
            <AIRevenueGrowthROICalculator />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

// AI Growth Impact Assessment Component
function AIGrowthImpactAssessment() {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [showResults, setShowResults] = useState(false);

  const questions = [
    {
      category: 'Revenue Growth Potential',
      questions: [
        { 
          id: 'revenue1', 
          text: 'How predictable is revenue month to month?',
          answerType: 'predict'
        },
        { 
          id: 'revenue2', 
          text: 'Where do most of your qualified opportunities come from?',
          answerType: 'leadsource'
        },
        { 
          id: 'revenue3', 
          text: 'How well-defined is your sales process?',
          answerType: 'salesprocess'
        },
        { 
          id: 'revenue4', 
          text: 'How often are upsell or cross-sell opportunities missed with existing customers?',
          answerType: 'amount'
        },
        { 
          id: 'revenue5', 
          text: 'How consistent and timely is your lead followup?',
          answerType: 'leadfu'
        }
      ]
    },
    {
      category: 'Market Expansion Opportunities',
      questions: [
        { 
          id: 'market1', 
          text: 'How integrated are core growth systems (CRM, outreach, reporting)?',
          answerType: 'integrate'
        },
        { 
          id: 'market2', 
          text: 'How is AI currently used in your business?',
          answerType: 'aiuse'
        }
      ]
    },
    {
      category: 'Operational Efficiency for Growth',
      questions: [
        { 
          id: 'efficiency1', 
          text: 'What amount of time does your team spend on repetitive tasks?',
          answerType: 'time'
        },
        { 
          id: 'efficiency2', 
          text: 'How involved is the founder in day-to-day sales and growth?',
          answerType: 'founder'
        }
      ]
    }
  ];

  const allQuestions = questions.flatMap(cat => cat.questions);

  const getAnswerOptions = (answerType: string) => {
    switch (answerType) {
      case 'predict':
        return [
          { score: 5, label: 'Not predictable nor consistent' },
          { score: 4, label: 'Low predictability' },
          { score: 3, label: 'Mostly predictable' },
          { score: 2, label: 'Highly predictable and consistent' }
        ];
      case 'leadfu':
        return [
          { score: 5, label: 'Slow or inconsistent follow-up' },
          { score: 4, label: 'Follow-up happens but is manual or delayed' },
          { score: 3, label: 'Consistent follow-up with some automation' },
          { score: 2, label: 'Immediate, automated, and measured follow-up' }
        ];
      case 'salesprocess':
        return [
          { score: 5, label: 'Undocumented, tribal knowledge' },
          { score: 4, label: 'Somewhat documented but not consistently followed' },
          { score: 3, label: 'Generally documented and usually followed' },
          { score: 2, label: 'Well documented, measured, and optimized' }
        ];
      case 'leadsource':
        return [
          { score: 5, label: 'Founder-driven referrals' },
          { score: 4, label: 'Inconsistent outbounding or one-off marketing attempts' },
          { score: 3, label: 'One primary channel that works but is not optimized' },
          { score: 2, label: 'Multiple predictable, measureable, and scalable channels' }
        ];
      case 'amount':
        return [
          { score: 5, label: 'Very Often (Significant untapped potential)' },
          { score: 4, label: 'Sometimes (Moderate opportunity)' },
          { score: 3, label: 'Rarely (Limited opportunity)' },
          { score: 2, label: 'Never (Minimal opportunity)' }
        ];
      case 'integrate':
        return [
          { score: 5, label: 'Mostly manual or disconnected tools' },
          { score: 4, label: 'Basic tools with limited integration' },
          { score: 3, label: 'Integrated systems with some automation' },
          { score: 2, label: 'Most systems fully integrated and automated' }
        ];
      case 'aiuse':
        return [
          { score: 5, label: 'Not using AI in any meaningful way' },
          { score: 4, label: 'Individual or experimental usage only (ChatGPT, tools, prompts)' },
          { score: 3, label: 'AI supports some workflows' },
          { score: 2, label: 'AI is embedded into most core growth systems' }
        ];
      case 'time':
        return [
          { score: 5, label: 'More than 50%+ of the time' },
          { score: 4, label: 'A lot (25%+ of the time)' },
          { score: 3, label: 'Some (10-25% of time)' },
          { score: 2, label: 'Less than 10% of the time' }
        ];
      case 'founder':
        return [
          { score: 5, label: 'Founder is essential to most deals' },
          { score: 4, label: 'Founder is heavily involved in sales and strategy' },
          { score: 3, label: 'Founder is involved selectively' },
          { score: 2, label: 'Growth runs independently of the founder' }
        ];
      default:
        return [
          { score: 5, label: 'Significant Opportunity (High Impact)' },
          { score: 4, label: 'Considerable Opportunity' },
          { score: 3, label: 'Moderate Opportunity' },
          { score: 2, label: 'Limited Opportunity' },
          { score: 1, label: 'Minimal Opportunity' }
        ];
    }
  };

  const handleAnswer = (questionId: string, score: number) => {
    setAnswers(prev => ({ ...prev, [questionId]: score }));
    
    // Auto-advance to next question
    setTimeout(() => {
      if (currentStep < allQuestions.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        setShowResults(true);
      }
    }, 300);
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const calculateScore = () => {
    const totalScore = Object.values(answers).reduce((sum, score) => sum + score, 0);
    const maxScore = allQuestions.length * 5;
    return Math.round((totalScore / maxScore) * 100);
  };

  const getGrowthImpact = (score: number) => {
    if (score >= 80) return { 
      level: 'Transformational Growth Potential', 
      color: 'text-success', 
      description: 'AI could unlock 40-60% revenue growth within 18 months through market expansion and efficiency gains.',
      projectedGrowth: '40-60%'
    };
    if (score >= 64) return { 
      level: 'High Growth Potential', 
      color: 'text-accent', 
      description: 'AI implementation could drive 25-40% revenue increase through improved conversion and customer expansion.',
      projectedGrowth: '25-40%'
    };
    if (score >= 44) return { 
      level: 'Moderate Growth Potential', 
      color: 'text-primary', 
      description: 'AI can deliver 15-25% growth by optimizing existing processes and improving customer engagement.',
      projectedGrowth: '15-25%'
    };
    return { 
      level: 'Foundational Growth Potential', 
      color: 'text-muted-foreground', 
      description: 'AI can provide 10-15% efficiency gains as a foundation for future growth initiatives.',
      projectedGrowth: '10-15%'
    };
  };

  const currentQuestion = allQuestions[currentStep];
  const progress = ((currentStep + 1) / allQuestions.length) * 100;

  if (showResults) {
    const score = calculateScore();
    const growthImpact = getGrowthImpact(score);

    return (
      <div className="bg-card rounded-2xl shadow-elevated p-8 lg:p-12">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-6">
            <Icon name="ClipboardDocumentCheckIcon" size={20} className="text-primary" />
            <span className="text-sm font-semibold text-primary">Assessment Complete</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Your AI Growth Impact Score
          </h2>
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-8 mb-8 text-center">
            <div className="text-7xl font-heading font-bold text-foreground mb-4">{score}%</div>
            <div className={`text-2xl font-heading font-bold ${growthImpact.color} mb-2`}>{growthImpact.level}</div>
            <p className="text-muted-foreground mb-4">{growthImpact.description}</p>
            <div className="inline-flex items-center space-x-2 bg-success/10 px-6 py-3 rounded-lg">
              <Icon name="TrendingUpIcon" size={24} className="text-success" />
              <span className="text-lg font-heading font-bold text-success">Projected Growth: {growthImpact.projectedGrowth}</span>
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="text-xl font-heading font-bold text-foreground">Recommended Growth Strategies</h3>
            
            {score >= 80 && (
              <div className="space-y-4">
                <div className="flex items-start space-x-3 bg-success/5 border border-success/20 rounded-lg p-4">
                  <Icon name="RocketLaunchIcon" size={24} className="text-success flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Launch AI-Powered Sales Acceleration</h4>
                    <p className="text-sm text-muted-foreground">Implement predictive lead scoring and automated outreach to increase deal velocity by 3-5x.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 bg-success/5 border border-success/20 rounded-lg p-4">
                  <Icon name="ChartBarIcon" size={24} className="text-success flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Scale Customer Success with AI</h4>
                    <p className="text-sm text-muted-foreground">Deploy intelligent upsell recommendations and proactive engagement to expand account value 40-60%.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 bg-success/5 border border-success/20 rounded-lg p-4">
                  <Icon name="GlobeAltIcon" size={24} className="text-success flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Expand Market Reach</h4>
                    <p className="text-sm text-muted-foreground">Use AI automation to serve 3-5x more customers without proportional cost increases.</p>
                  </div>
                </div>
              </div>
            )}

            {score >= 60 && score < 80 && (
              <div className="space-y-4">
                <div className="flex items-start space-x-3 bg-accent/5 border border-accent/20 rounded-lg p-4">
                  <Icon name="BoltIcon" size={24} className="text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Optimize Conversion Rates</h4>
                    <p className="text-sm text-muted-foreground">Implement AI-driven personalization to increase conversion rates by 25-35%.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 bg-accent/5 border border-accent/20 rounded-lg p-4">
                  <Icon name="UserGroupIcon" size={24} className="text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Enhance Customer Lifetime Value</h4>
                    <p className="text-sm text-muted-foreground">Deploy predictive analytics to identify expansion opportunities and reduce churn.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 bg-accent/5 border border-accent/20 rounded-lg p-4">
                  <Icon name="ClockIcon" size={24} className="text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Accelerate Response Times</h4>
                    <p className="text-sm text-muted-foreground">Automate lead qualification and follow-up to capture opportunities 10x faster.</p>
                  </div>
                </div>
              </div>
            )}

            {score >= 40 && score < 60 && (
              <div className="space-y-4">
                <div className="flex items-start space-x-3 bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <Icon name="CogIcon" size={24} className="text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Automate Repetitive Tasks</h4>
                    <p className="text-sm text-muted-foreground">Free up 30-40% of team capacity by automating data entry, reporting, and routine communications.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <Icon name="LightBulbIcon" size={24} className="text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Improve Data-Driven Decisions</h4>
                    <p className="text-sm text-muted-foreground">Implement AI analytics to identify high-value opportunities and optimize resource allocation.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <Icon name="SparklesIcon" size={24} className="text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Enhance Customer Experience</h4>
                    <p className="text-sm text-muted-foreground">Deploy AI chatbots and personalized engagement to improve satisfaction and retention.</p>
                  </div>
                </div>
              </div>
            )}

            {score < 40 && (
              <div className="space-y-4">
                <div className="flex items-start space-x-3 bg-muted/50 border border-border rounded-lg p-4">
                  <Icon name="DocumentTextIcon" size={24} className="text-muted-foreground flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Establish Process Foundation</h4>
                    <p className="text-sm text-muted-foreground">Document and standardize key workflows to prepare for AI-driven optimization.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 bg-muted/50 border border-border rounded-lg p-4">
                  <Icon name="AcademicCapIcon" size={24} className="text-muted-foreground flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Build AI Awareness</h4>
                    <p className="text-sm text-muted-foreground">Educate leadership on AI growth opportunities and develop strategic vision.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 bg-muted/50 border border-border rounded-lg p-4">
                  <Icon name="BeakerIcon" size={24} className="text-muted-foreground flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Start Small Pilot</h4>
                    <p className="text-sm text-muted-foreground">Test AI in one area (e.g., email automation) to demonstrate value and build momentum.</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-12 text-center">
            <Link
              href="/contact"
              className="inline-flex items-center px-8 py-4 bg-accent text-white font-heading font-semibold rounded-lg hover:bg-accent/90 hover:shadow-elevated transition-all duration-300"
            >
              Get Your Custom Growth Plan
              <Icon name="ArrowRightIcon" size={20} className="ml-2" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-2xl shadow-elevated p-8 lg:p-12">
      <div className="max-w-3xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-muted-foreground">Question {currentStep + 1} of {allQuestions.length}</span>
            <span className="text-sm font-semibold text-accent">{Math.round(progress)}% Complete</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div className="bg-accent h-2 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Question */}
        <div className="mb-8">
          <h3 className="text-2xl font-heading font-bold text-foreground mb-6">
            {currentQuestion.text}
          </h3>

          <div className="space-y-3">
            {getAnswerOptions(currentQuestion.answerType).map((option) => (
              <button
                key={option.score}
                onClick={() => handleAnswer(currentQuestion.id, option.score)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-300 ${
                  answers[currentQuestion.id] === option.score
                    ? 'border-accent bg-accent/10' :'border-border hover:border-accent/50 hover:bg-muted'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-foreground">
                    {option.label}
                  </span>
                  {answers[currentQuestion.id] === option.score && (
                    <Icon name="CheckCircleIcon" size={24} className="text-accent" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="flex items-center px-6 py-3 text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Icon name="ArrowLeftIcon" size={20} className="mr-2" />
            Previous
          </button>
          <div className="text-sm text-muted-foreground">
            Select an answer to continue
          </div>
        </div>
      </div>
    </div>
  );
}

// Support Cost Savings Calculator Component
function SupportCostSavingsCalculator() {
  const [inputs, setInputs] = useState({
    monthlyTickets: 5000,
    avgHandleTime: 15,
    agentHourlyCost: 25,
    currentSatisfaction: 75
  });

  const [results, setResults] = useState({
    monthlySavings: 0,
    annualSavings: 0,
    timeReduction: 0,
    satisfactionIncrease: 0
  });

  useEffect(() => {
    const automationRate = 0.70;
    const timeReductionRate = 0.85;
    const satisfactionBoost = 23;

    const currentMonthlyCost = (inputs.monthlyTickets * inputs.avgHandleTime / 60) * inputs.agentHourlyCost;
    const automatedTickets = inputs.monthlyTickets * automationRate;
    const timePerTicket = inputs.avgHandleTime * (1 - timeReductionRate);
    const newMonthlyCost = (automatedTickets * timePerTicket / 60) * inputs.agentHourlyCost;
    const monthlySavings = currentMonthlyCost - newMonthlyCost;
    const annualSavings = monthlySavings * 12;

    setResults({
      monthlySavings: Math.round(monthlySavings),
      annualSavings: Math.round(annualSavings),
      timeReduction: Math.round(timeReductionRate * 100),
      satisfactionIncrease: Math.min(98, inputs.currentSatisfaction + satisfactionBoost)
    });
  }, [inputs]);

  const handleInputChange = (field: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setInputs(prev => ({ ...prev, [field]: numValue }));
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="bg-card rounded-2xl shadow-elevated p-8 lg:p-12">
      <div className="text-center max-w-3xl mx-auto mb-12">
        <div className="inline-flex items-center space-x-2 bg-success/10 px-4 py-2 rounded-full mb-6">
          <Icon name="CurrencyDollarIcon" size={20} className="text-success" />
          <span className="text-sm font-semibold text-success">Support Cost Savings Calculator</span>
        </div>
        <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
          Calculate Your Support Efficiency Gains
        </h2>
        <p className="text-lg text-muted-foreground">
          Discover how AI automation can reduce costs and improve customer satisfaction.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-12">
        {/* Input Section */}
        <div className="space-y-6">
          <h3 className="text-xl font-heading font-bold text-foreground mb-6">Current Support Metrics</h3>

          <div className="space-y-2">
            <label className="flex items-center justify-between text-sm font-semibold text-foreground">
              <span>Monthly Support Tickets</span>
              <span className="text-success">{inputs.monthlyTickets.toLocaleString()}</span>
            </label>
            <input
              type="range"
              min="500"
              max="50000"
              step="500"
              value={inputs.monthlyTickets}
              onChange={(e) => handleInputChange('monthlyTickets', e.target.value)}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-success"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>500</span>
              <span>50,000</span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center justify-between text-sm font-semibold text-foreground">
              <span>Avg Handle Time (minutes)</span>
              <span className="text-success">{inputs.avgHandleTime}</span>
            </label>
            <input
              type="range"
              min="5"
              max="60"
              step="5"
              value={inputs.avgHandleTime}
              onChange={(e) => handleInputChange('avgHandleTime', e.target.value)}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-success"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>5 min</span>
              <span>60 min</span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center justify-between text-sm font-semibold text-foreground">
              <span>Agent Hourly Cost ($)</span>
              <span className="text-success">${inputs.agentHourlyCost}</span>
            </label>
            <input
              type="range"
              min="15"
              max="100"
              step="5"
              value={inputs.agentHourlyCost}
              onChange={(e) => handleInputChange('agentHourlyCost', e.target.value)}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-success"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>$15</span>
              <span>$100</span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center justify-between text-sm font-semibold text-foreground">
              <span>Current Satisfaction Score (%)</span>
              <span className="text-success">{inputs.currentSatisfaction}%</span>
            </label>
            <input
              type="range"
              min="50"
              max="95"
              step="5"
              value={inputs.currentSatisfaction}
              onChange={(e) => handleInputChange('currentSatisfaction', e.target.value)}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-success"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>50%</span>
              <span>95%</span>
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div className="space-y-6">
          <h3 className="text-xl font-heading font-bold text-foreground mb-6">Projected Savings</h3>

          <div className="bg-gradient-to-br from-success/10 to-accent/10 rounded-xl p-6 border border-success/20">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-success rounded-lg flex items-center justify-center">
                <Icon name="BanknotesIcon" size={24} className="text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Annual Cost Savings</p>
                <p className="text-3xl font-heading font-bold text-foreground">{formatCurrency(results.annualSavings)}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-muted/50 rounded-xl p-4">
              <p className="text-sm text-muted-foreground mb-2">Monthly Savings</p>
              <p className="text-xl font-heading font-bold text-foreground">{formatCurrency(results.monthlySavings)}</p>
            </div>
            <div className="bg-muted/50 rounded-xl p-4">
              <p className="text-sm text-muted-foreground mb-2">Time Reduction</p>
              <p className="text-xl font-heading font-bold text-success">{results.timeReduction}%</p>
            </div>
          </div>

          <div className="bg-muted/50 rounded-xl p-4">
            <p className="text-sm text-muted-foreground mb-2">Projected Satisfaction Score</p>
            <div className="flex items-center justify-between">
              <p className="text-xl font-heading font-bold text-foreground">{results.satisfactionIncrease}%</p>
              <span className="text-sm text-success font-semibold">+{results.satisfactionIncrease - inputs.currentSatisfaction}%</span>
            </div>
          </div>

          <div className="bg-success/5 border border-success/20 rounded-xl p-6">
            <h4 className="font-heading font-bold text-foreground mb-3">Key Benefits</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start">
                <Icon name="CheckCircleIcon" size={20} className="text-success mr-2 flex-shrink-0 mt-0.5" />
                <span>Automate 70% of routine support inquiries</span>
              </li>
              <li className="flex items-start">
                <Icon name="CheckCircleIcon" size={20} className="text-success mr-2 flex-shrink-0 mt-0.5" />
                <span>Reduce average handle time by 85%</span>
              </li>
              <li className="flex items-start">
                <Icon name="CheckCircleIcon" size={20} className="text-success mr-2 flex-shrink-0 mt-0.5" />
                <span>24/7 instant response capability</span>
              </li>
              <li className="flex items-start">
                <Icon name="CheckCircleIcon" size={20} className="text-success mr-2 flex-shrink-0 mt-0.5" />
                <span>Improve customer satisfaction by 23%</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-12 text-center">
        <Link
          href="/contact"
          className="inline-flex items-center px-8 py-4 bg-success text-white font-heading font-semibold rounded-lg hover:bg-success/90 hover:shadow-elevated transition-all duration-300"
        >
          Start Saving Today
          <Icon name="ArrowRightIcon" size={20} className="ml-2" />
        </Link>
      </div>
    </div>
  );
}

// Sales Pipeline Health Check Component
function SalesPipelineHealthCheck() {
  const [inputs, setInputs] = useState({
    monthlyLeads: 500,
    leadToMQL: 40,
    mqlToSQL: 50,
    sqlToOpportunity: 60,
    opportunityToClose: 25,
    avgDealSize: 50000
  });

  const [results, setResults] = useState({
    currentConversion: 0,
    projectedConversion: 0,
    currentRevenue: 0,
    projectedRevenue: 0,
    bottleneck: '',
    improvement: 0
  });

  useEffect(() => {
    const currentConversion = (inputs.leadToMQL / 100) * (inputs.mqlToSQL / 100) * (inputs.sqlToOpportunity / 100) * (inputs.opportunityToClose / 100);
    const projectedConversion = currentConversion * 1.85;
    const currentClosedDeals = inputs.monthlyLeads * currentConversion;
    const projectedClosedDeals = inputs.monthlyLeads * projectedConversion;
    const currentRevenue = currentClosedDeals * inputs.avgDealSize;
    const projectedRevenue = projectedClosedDeals * inputs.avgDealSize;

    // Identify bottleneck
    const stages = [
      { name: 'Lead to MQL', rate: inputs.leadToMQL },
      { name: 'MQL to SQL', rate: inputs.mqlToSQL },
      { name: 'SQL to Opportunity', rate: inputs.sqlToOpportunity },
      { name: 'Opportunity to Close', rate: inputs.opportunityToClose }
    ];
    const bottleneck = stages.reduce((min, stage) => stage.rate < min.rate ? stage : min).name;

    setResults({
      currentConversion: currentConversion * 100,
      projectedConversion: projectedConversion * 100,
      currentRevenue: currentRevenue * 12,
      projectedRevenue: projectedRevenue * 12,
      bottleneck,
      improvement: ((projectedRevenue - currentRevenue) / currentRevenue) * 100
    });
  }, [inputs]);

  const handleInputChange = (field: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setInputs(prev => ({ ...prev, [field]: numValue }));
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="bg-card rounded-2xl shadow-elevated p-8 lg:p-12">
      <div className="text-center max-w-3xl mx-auto mb-12">
        <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full mb-6">
          <Icon name="ChartBarIcon" size={20} className="text-accent" />
          <span className="text-sm font-semibold text-accent">Sales Pipeline Health Check</span>
        </div>
        <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
          Analyze Your Sales Pipeline Performance
        </h2>
        <p className="text-lg text-muted-foreground">
          Identify bottlenecks and opportunities to optimize your conversion rates.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-12">
        {/* Input Section */}
        <div className="space-y-6">
          <h3 className="text-xl font-heading font-bold text-foreground mb-6">Pipeline Metrics</h3>

          <div className="space-y-2">
            <label className="flex items-center justify-between text-sm font-semibold text-foreground">
              <span>Monthly Leads</span>
              <span className="text-accent">{inputs.monthlyLeads}</span>
            </label>
            <input
              type="range"
              min="50"
              max="5000"
              step="50"
              value={inputs.monthlyLeads}
              onChange={(e) => handleInputChange('monthlyLeads', e.target.value)}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-accent"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>50</span>
              <span>5,000</span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center justify-between text-sm font-semibold text-foreground">
              <span>Lead to MQL (%)</span>
              <span className="text-accent">{inputs.leadToMQL}%</span>
            </label>
            <input
              type="range"
              min="10"
              max="80"
              step="5"
              value={inputs.leadToMQL}
              onChange={(e) => handleInputChange('leadToMQL', e.target.value)}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-accent"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>10%</span>
              <span>80%</span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center justify-between text-sm font-semibold text-foreground">
              <span>MQL to SQL (%)</span>
              <span className="text-accent">{inputs.mqlToSQL}%</span>
            </label>
            <input
              type="range"
              min="10"
              max="80"
              step="5"
              value={inputs.mqlToSQL}
              onChange={(e) => handleInputChange('mqlToSQL', e.target.value)}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-accent"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>10%</span>
              <span>80%</span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center justify-between text-sm font-semibold text-foreground">
              <span>SQL to Opportunity (%)</span>
              <span className="text-accent">{inputs.sqlToOpportunity}%</span>
            </label>
            <input
              type="range"
              min="10"
              max="80"
              step="5"
              value={inputs.sqlToOpportunity}
              onChange={(e) => handleInputChange('sqlToOpportunity', e.target.value)}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-accent"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>10%</span>
              <span>80%</span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center justify-between text-sm font-semibold text-foreground">
              <span>Opportunity to Close (%)</span>
              <span className="text-accent">{inputs.opportunityToClose}%</span>
            </label>
            <input
              type="range"
              min="10"
              max="80"
              step="5"
              value={inputs.opportunityToClose}
              onChange={(e) => handleInputChange('opportunityToClose', e.target.value)}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-accent"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>10%</span>
              <span>80%</span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center justify-between text-sm font-semibold text-foreground">
              <span>Average Deal Size</span>
              <span className="text-accent">{formatCurrency(inputs.avgDealSize)}</span>
            </label>
            <input
              type="range"
              min="5000"
              max="500000"
              step="5000"
              value={inputs.avgDealSize}
              onChange={(e) => handleInputChange('avgDealSize', e.target.value)}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-accent"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>$5K</span>
              <span>$500K</span>
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div className="space-y-6">
          <h3 className="text-xl font-heading font-bold text-foreground mb-6">Health Analysis</h3>

          <div className="bg-gradient-to-br from-accent/10 to-primary/10 rounded-xl p-6 border border-accent/20">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
                <Icon name="TrendingUpIcon" size={24} className="text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Potential Annual Revenue Increase</p>
                <p className="text-3xl font-heading font-bold text-foreground">{formatCurrency(results.projectedRevenue - results.currentRevenue)}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-muted/50 rounded-xl p-4">
              <p className="text-sm text-muted-foreground mb-2">Current Conversion</p>
              <p className="text-xl font-heading font-bold text-foreground">{results.currentConversion.toFixed(2)}%</p>
            </div>
            <div className="bg-muted/50 rounded-xl p-4">
              <p className="text-sm text-muted-foreground mb-2">Projected Conversion</p>
              <p className="text-xl font-heading font-bold text-success">{results.projectedConversion.toFixed(2)}%</p>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <div className="flex items-start space-x-3">
              <Icon name="ExclamationTriangleIcon" size={24} className="text-red-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-heading font-bold text-foreground mb-2">Identified Bottleneck</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Your biggest opportunity for improvement is in the <span className="font-semibold text-foreground">{results.bottleneck}</span> stage.
                </p>
                <p className="text-sm text-muted-foreground">
                  AI-powered lead qualification and nurturing can improve this conversion rate by up to 85%.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-accent/5 border border-accent/20 rounded-xl p-6">
            <h4 className="font-heading font-bold text-foreground mb-3">Optimization Opportunities</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start">
                <Icon name="CheckCircleIcon" size={20} className="text-accent mr-2 flex-shrink-0 mt-0.5" />
                <span>Automate lead scoring and qualification</span>
              </li>
              <li className="flex items-start">
                <Icon name="CheckCircleIcon" size={20} className="text-accent mr-2 flex-shrink-0 mt-0.5" />
                <span>Implement intelligent follow-up sequences</span>
              </li>
              <li className="flex items-start">
                <Icon name="CheckCircleIcon" size={20} className="text-accent mr-2 flex-shrink-0 mt-0.5" />
                <span>Personalize outreach at scale</span>
              </li>
              <li className="flex items-start">
                <Icon name="CheckCircleIcon" size={20} className="text-accent mr-2 flex-shrink-0 mt-0.5" />
                <span>Predict deal likelihood and prioritize efforts</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-12 text-center">
        <Link
          href="/contact"
          className="inline-flex items-center px-8 py-4 bg-accent text-white font-heading font-semibold rounded-lg hover:bg-accent/90 hover:shadow-elevated transition-all duration-300"
        >
          Optimize Your Pipeline
          <Icon name="ArrowRightIcon" size={20} className="ml-2" />
        </Link>
      </div>
    </div>
  );
}